/**
 * El **diseño** del certificado de finalización, como dato.
 *
 * Sale del artboard "Certificado Atlas" que se diseñó en Claude Design
 * (ADR-2026-08-25-el-certificado-sale-del-artboard-de-claude-design). El arte —marco
 * azul, cenefas, fondo de ondas, logo, sello y la firma— viaja como **PDF vectorial**
 * (`FONDO`) y todo el TEXTO se dibuja acá encima con pdf-lib.
 *
 * ## Por qué el texto no está en el PDF de fondo
 *
 * El fondo se imprimió con Chrome desde el mismo artboard, y ahí el texto ya venía
 * dibujado. Se lo sacó a propósito: dibujándolo con pdf-lib queda en UNA sola
 * tipografía embebida y subseteada —compartida por el texto fijo y el variable—, el
 * PDF pesa menos, y el certificado entero es texto de verdad: seleccionable,
 * copiable y buscable. Además el texto fijo queda acá, editable sin volver a Figma
 * ni a Claude Design.
 *
 * ## Unidades
 *
 * **Todo va en píxeles del artboard (1056 × 816)**, exactamente los números que
 * devuelve el inspector del diseño. El motor los pasa a puntos PDF multiplicando por
 * `ESCALA` en un solo lugar. La página es Letter apaisado: 1056 px / 96 dpi = 11" =
 * 792 pt de ancho; 816 px = 8.5" = 612 pt de alto.
 *
 * La `y` de un texto es su **línea de base**, medida desde el borde de arriba. Es lo
 * que se midió en el navegador sobre el artboard real (un `inline-block` de altura
 * cero alineado a la baseline cae exactamente sobre ella), así que no hay que deducir
 * nada de la altura de línea ni de las métricas de la fuente: el número es el número.
 */

/** Los tres datos que cambian en cada certificado. */
export type CampoCertificado = 'nombre' | 'curso' | 'fecha';

/**
 * Las fuentes que usa el diseño, desde `public/fonts`.
 *
 * Van las ESTÁTICAS y no las variables (`*-VariableFont_wght.ttf`): fontkit embebe la
 * instancia por defecto de una fuente variable, así que el peso 800 del título saldría
 * en Regular. Los pesos son los que declara el artboard: 800, 700 y 600 de Plus
 * Jakarta Sans, y la itálica de Playfair para la firma.
 */
export const FUENTES = {
  sansSemi: '/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-SemiBold.ttf',
  sansBold: '/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-Bold.ttf',
  sansExtra: '/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-ExtraBold.ttf',
  serifItalic: '/fonts/Playfair_Display/static/PlayfairDisplay-Italic.ttf',
} as const;

export type ClaveFuente = keyof typeof FUENTES;

/**
 * Un texto del certificado: fijo (`"Certificado"`) o variable
 * (`{ campo: 'nombre' }`).
 *
 * Se posiciona por **caja**, no por punto: `x` + `ancho` definen la columna y el texto
 * va centrado dentro de ella. Es lo que hace que un nombre corto y uno largo queden
 * los dos centrados sin tocar nada, y reproduce el `align-items:center` del artboard.
 */
export interface CapaTexto {
  /** Literal, o el campo variable que lo reemplaza en cada certificado. */
  contenido: string | { campo: CampoCertificado };
  /** Borde izquierdo de la caja, en px del artboard. */
  x: number;
  /** Ancho de la caja, en px. El texto se centra adentro. */
  ancho: number;
  /** **Línea de base**, en px desde el borde de arriba. */
  y: number;
  fuente: ClaveFuente;
  /** Cuerpo en px del artboard (el `font-size` del diseño). */
  tamano: number;
  /**
   * Si el texto no entra en `ancho`, el motor baja el cuerpo hasta acá antes de
   * cortarlo con "…". Sólo lo llevan los campos variables: el texto fijo entra
   * siempre, porque el diseño se hizo con él puesto.
   */
  tamanoMinimo?: number;
  /** Hex `#RRGGBB`. */
  color: string;
  /** 0–1 — el diseño usa `rgba()` para el gris de "Completó con éxito". */
  opacidad?: number;
  /** `letter-spacing` del diseño, en px. Negativo aprieta. */
  espaciado?: number;
}

export interface PlantillaCertificado {
  /** Tamaño del artboard, en px. */
  ancho: number;
  alto: number;
  /** px → puntos PDF. 96 px por pulgada, 72 pt por pulgada. */
  escala: number;
  /**
   * El arte, como PDF vectorial de una página y del tamaño exacto de la página.
   *
   * Se imprimió con Chrome desde el artboard con **todo el texto oculto**, así que
   * trae sólo los ornamentos: el marco azul, las cenefas, el fondo de ondas, el logo,
   * el sello, la firma y las dos líneas de los pies.
   *
   * Va en PDF y no en PNG porque el certificado se imprime: en vectores sale nítido a
   * cualquier tamaño y no hay una resolución que elegir. Para regenerarlo, ver
   * `docs/2026-08-25-certificado-de-claude-design-proceso.md`.
   */
  fondo: string;
  capas: CapaTexto[];
}

/* Los colores del artboard, tal como los declara. `TINTA` es el `color` de la página
   (#020B2E) y NO el `brand-black` de la app (#02062B): son parecidos pero distintos, y
   el que manda acá es el del diseño. */
const TINTA = '#020B2E';
const AZUL = '#1644EB'; // hex-de-marca-ok: es la lámina del certificado, igual al PDF que se descarga

/** La caja de contenido del artboard: `padding: 120px 142px 112px` sobre 1056 px. */
const COL_X = 142;
const COL_ANCHO = 772;
/** Las dos columnas del pie (fecha y firma), de 220 px con 110 px de separación. */
const PIE_ANCHO = 220;
const PIE_FECHA_X = 253;
const PIE_FIRMA_X = 583;

export const PLANTILLA: PlantillaCertificado = {
  ancho: 1056,
  alto: 816,
  escala: 72 / 96,
  fondo: '/certificados/certificado-atlas-base.pdf',
  capas: [
    // ── Encabezado ────────────────────────────────────────────────────────────
    {
      contenido: 'Certificado',
      x: COL_X,
      ancho: COL_ANCHO,
      y: 286.55,
      fuente: 'sansExtra',
      tamano: 76,
      espaciado: -1.9,
      color: TINTA,
    },
    {
      contenido: 'de Finalización',
      x: COL_X,
      ancho: COL_ANCHO,
      y: 328.55,
      fuente: 'sansBold',
      tamano: 24,
      color: TINTA,
    },

    // ── EL NOMBRE ─────────────────────────────────────────────────────────────
    /* Lo más grande y lo único en azul del cuerpo: el certificado es de la persona, y
       lo primero que se busca al abrirlo es el propio nombre. */
    {
      contenido: { campo: 'nombre' },
      x: COL_X,
      ancho: COL_ANCHO,
      y: 442.55,
      fuente: 'sansExtra',
      tamano: 54,
      tamanoMinimo: 30,
      espaciado: -1.08,
      color: AZUL,
    },

    // ── EL CURSO ──────────────────────────────────────────────────────────────
    {
      contenido: 'Completó con éxito',
      x: COL_X,
      ancho: COL_ANCHO,
      y: 530.94,
      fuente: 'sansSemi',
      tamano: 15,
      color: TINTA,
      opacidad: 0.45,
    },
    {
      contenido: { campo: 'curso' },
      x: COL_X,
      ancho: COL_ANCHO,
      y: 576.94,
      fuente: 'sansBold',
      tamano: 32,
      tamanoMinimo: 17,
      espaciado: -0.48,
      color: TINTA,
    },

    // ── Pie izquierdo: LA FECHA ───────────────────────────────────────────────
    {
      contenido: { campo: 'fecha' },
      x: PIE_FECHA_X,
      ancho: PIE_ANCHO,
      y: 663.5,
      fuente: 'sansBold',
      tamano: 19,
      tamanoMinimo: 13,
      color: TINTA,
    },
    {
      contenido: 'Fecha de emisión',
      x: PIE_FECHA_X,
      ancho: PIE_ANCHO,
      y: 701,
      fuente: 'sansBold',
      tamano: 15,
      color: TINTA,
    },

    // ── Pie derecho: la firma ─────────────────────────────────────────────────
    /* Va como texto y no como imagen de firma escaneada porque así lo resolvió el
       diseño: Playfair itálica sobre la línea. */
    {
      contenido: 'Nacho Porras',
      x: PIE_FIRMA_X,
      ancho: PIE_ANCHO,
      y: 664.5,
      fuente: 'serifItalic',
      tamano: 25,
      color: TINTA,
    },
    {
      contenido: 'Fundador y CEO',
      x: PIE_FIRMA_X,
      ancho: PIE_ANCHO,
      y: 701,
      fuente: 'sansBold',
      tamano: 15,
      color: TINTA,
    },
  ],
};
